import { UserRole } from '@uplift/types';
import { restClient } from '../client';


export type Conversation = {
  id: string;
  name: string;
  disabled: boolean;
  unreadMessagesCount: number;
  participantId: string;
  participantRole: UserRole;
};

export type MessageAttachment = {
  id: string;
  name: string;
};

export type Message = {
  id: string;
  senderId: string;
  receiverId: string;
  message: string;
  sentAt: string;
  seenAt: string | null;
  conversationId: string;
  attachments: MessageAttachment[];
};

export type MessagingToDoStatus = 'active' | 'completed' | 'archived';
export type MessagingToDo = {
  id: string;
  status: MessagingToDoStatus;
  description: string;
  participantId: string;
  message: Message;
  createdAt: string;

  completedAt?: string;
  archivedAt?: string;
  updatedAt?: string;
};


export const getMessagingConversations = () => restClient.request<{
  data: Conversation[];
}>('/api/v1/messaging/conversation');


export const createMessagingConversations = (participantId: string) => restClient.request<{
  data: {
    conversationId: string;
  };
}>('/api/v1/messaging/conversation', {
  method: 'POST',
  data: {
    participant_id: participantId,
  }
});


export const getMessagesUpdates = () => restClient.request<{
  data: string | null;
}>('/api/v1/messaging/updates');


export const getMessages = (conversationId: string) => restClient.request<{
  data: Message[];
}>(`/api/v1/messaging/conversation/${conversationId}/message`);


export const getConversationToDoIds = (conversationId: string) => restClient.request<{
  data: string[];
}>(`/api/v1/messaging/conversation/${conversationId}/todo`);


export const sendMessage = (conversationId: string, message: string, attachmentIds?: string[]) => restClient.request<{
  data: Message[];
}>(`/api/v1/messaging/conversation/${conversationId}/message`, {
  method: 'POST',
  data: {
    message: message,
    attachment_ids: attachmentIds,
  },
});


export const markMessagesAsRead = (messageIds: string[]) => restClient.request<null>('/api/v1/messaging/mark-as-read', {
  method: 'PATCH',
  data: {
    message_ids: messageIds,
  },
});


export const getMessageAttachmentDownloadLink = (attachmentId: string) => restClient.request<{
  data: {
    downloadLink: string;
  };
}>(`/api/v1/messaging/attachments/${attachmentId}/download_link`, {
  method: 'POST',
});


export const getMessagingToDosWithStatus = (status?: MessagingToDoStatus) => restClient.request<{
  data: string[];
}>('/api/v1/messaging/todo', {
  params: {
    status: status,
  }
});


export const getMessagingToDos = (todoIds: string[]) => restClient.request<{
  data: MessagingToDo[];
}>('/api/v1/messaging/todo/by_ids', {
  data: {
    todo_ids: todoIds,
  },
  method: 'POST',
});


export const createMessagingToDo = (messageId: string, description: string) => restClient.request<{}>('/api/v1/messaging/todo', {
  method: 'POST',
  data: {
    message_id: messageId,
    description: description,
  }
});

export const archiveMessagingToDo = (todoId: string) => restClient.request<{}>(`/api/v1/messaging/todo/${todoId}/archive`, {
  method: 'POST',
});

export const unarchiveMessagingToDo = (todoId: string) => restClient.request<{}>(`/api/v1/messaging/todo/${todoId}/unarchive`, {
  method: 'POST',
});

export const completeMessagingToDo = (todoId: string) => restClient.request<{}>(`/api/v1/messaging/todo/${todoId}/complete`, {
  method: 'POST',
});

export const uncompleteMessagingToDo = (todoId: string) => restClient.request<{}>(`/api/v1/messaging/todo/${todoId}/uncomplete`, {
  method: 'POST',
});


export type ProviderMessagingReportItem = {
  userId: string;
  userName: string;
  oldestUnreadSentAt: string;
  newestUnreadSentAt: string;
  unreadMessagesCount: number;
};

export const adminMessagingGetUnreadReport = (userRole: UserRole) => restClient.request<{
  data: ProviderMessagingReportItem[];
}>(`/api/v1/admin/messaging/unread_report/by_role/${userRole}`);

export type UnreadConversationReportItem = {
  conversationId: string;
  senderId: string;
  userName: string;
  oldestUnreadSentAt: string;
  newestUnreadSentAt: string;
  unreadMessagesCount: number;
};

export const adminMessagingGetUnreadUserConversations = (userId: string) => restClient.request<{
  data: UnreadConversationReportItem[];
}>(`/api/v1/admin/messaging/unread_report/conversations/${userId}`);

export const adminMessagingGetConversationUnreadMessages = (conversationId: string, receiverId: string) => restClient.request<{
  data: Message[];
}>(`/api/v1/admin/messaging/unread_report/messages/${conversationId}/${receiverId}`);
